import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';

import Typography from '@componentLibrary/Typography';
import { cn } from '@componentLibrary/utils';

// Internal Radix UI components
function TooltipProvider({
    delayDuration = 0,
    ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
    return (
        <TooltipPrimitive.Provider
            data-slot="tooltip-provider"
            delayDuration={delayDuration}
            {...props}
        />
    );
}

function TooltipRoot({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
    return (
        <TooltipProvider>
            <TooltipPrimitive.Root data-slot="tooltip" {...props} />
        </TooltipProvider>
    );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
    return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
    className,
    sideOffset = 8,
    children,
    ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
    return (
        <TooltipPrimitive.Portal>
            <TooltipPrimitive.Content
                data-slot="tooltip-content"
                sideOffset={sideOffset}
                className={cn(
                    'bg-[#13263A] text-white px-2 py-1 rounded max-w-[320px] text-center',
                    'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
                    'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                    'z-[1500] w-fit',
                    className,
                )}
                {...props}
            >
                {children}
            </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
    );
}

// Main Tooltip component with simple API
type Side = 'top' | 'right' | 'bottom' | 'left';
type Align = 'start' | 'center' | 'end';

// Support legacy MUI-compatible placements used by the old wrapper
export type TooltipPlacement =
    | Side
    | 'top-start'
    | 'top-end'
    | 'right-start'
    | 'right-end'
    | 'bottom-start'
    | 'bottom-end'
    | 'left-start'
    | 'left-end';

export interface TooltipProps {
    /** The tooltip content - can be a string or JSX */
    title?: React.ReactNode;
    /** Tooltip placement (MUI-compatible) */
    placement?: TooltipPlacement;
    /** Whether the tooltip is disabled */
    disabled?: boolean;
    /** Custom offset from the trigger */
    sideOffset?: number;
    /** The trigger element */
    children: React.ReactElement;
    /** Additional CSS class for the tooltip content */
    className?: string;
    /** Additional CSS class for the trigger wrapper */
    triggerClassName?: string;
}

/**
 * A tooltip component that provides a simple API like Material-UI Tooltip
 * but uses Radix UI under the hood for better performance and consistency.
 *
 * @example
 * <Tooltip title="This is a tooltip" placement="top">
 *   <Button>Hover me</Button>
 * </Tooltip>
 */
const Tooltip: React.FC<TooltipProps> = ({
    title,
    placement = 'top',
    disabled = false,
    sideOffset,
    children,
    className,
    triggerClassName,
}) => {
    // If disabled or no title, just return the children
    if (disabled || !title) {
        return children;
    }

    // Map legacy placement strings like "top-start" to Radix side/align
    const parsePlacement = (value: TooltipPlacement): { side: Side; align: Align } => {
        if (!value) return { side: 'top', align: 'center' };
        const [maybeSide, maybeAlign] = String(value).split('-') as [
            Side | string,
            Align | string | undefined,
        ];
        const side: Side = (['top', 'right', 'bottom', 'left'] as const).includes(maybeSide as Side)
            ? (maybeSide as Side)
            : 'top';
        const align: Align = (['start', 'center', 'end'] as const).includes(
            (maybeAlign as Align) ?? 'center',
        )
            ? (maybeAlign as Align) ?? 'center'
            : 'center';
        return { side, align };
    };

    const { side, align } = parsePlacement(placement);

    return (
        <TooltipRoot>
            <TooltipTrigger asChild>
                <span
                    className={cn('inline-block', triggerClassName)}
                    aria-label={typeof title === 'string' ? title : undefined}
                >
                    {children}
                </span>
            </TooltipTrigger>
            <TooltipContent side={side} align={align} sideOffset={sideOffset} className={className}>
                {typeof title === 'string' ? (
                    <Typography variant="sm" as="span">
                        {title}
                    </Typography>
                ) : (
                    title
                )}
            </TooltipContent>
        </TooltipRoot>
    );
};

export { Tooltip };
